import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import dynamic from 'next/dynamic';
const VideoTestimonialsClient = dynamic(() => import('./VideoTestimonialsClient'), { ssr: false });
const VideoTestimonialsClientFallback = dynamic(() => import('./VideoTestimonialsClientFallback'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface VideoTestimonialsData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  button_mobile?: {
    label?: string;
    href?: string;
  };
  video_mobile?: {
    id?: string;
    filename_download?: string;
  };
  videos?: {
    directus_files_id: {
      id: string;
      filename_download: string;
    };
  }[];
  images?: {
    images_id: {
      alt: string;
      image_file: {
        image: {
          id: string;
          filename_download: string;
        };
      };
    };
  }[];
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",

  // text
  "text.text",

  // buttons
  "button.label",
  "button.href",
  "button_mobile.label",
  "button_mobile.href",
  
  // videos
  "video_mobile.id",
  "video_mobile.filename_download",
  
  "videos.directus_files_id.id",
  "videos.directus_files_id.filename_download",
  
  // images (thumbnails)
  "images.images_id.alt",
  "images.images_id.image_file.image.id",
  "images.images_id.image_file.image.filename_download",
] as const;

interface VideoTestimonialsProps {
  data?: VideoTestimonialsData;
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][];
  helperRotation?: number;
}

export default function VideoTestimonials({ 
  data, 
  zIndex, 
  thresholds, 
  transitionTime, 
  mobileThresholds, 
  helperRotation 
}: VideoTestimonialsProps) {
  // Prepare video data for client component
  const videosList = data?.videos?.map((video, index) => {
    // Get the corresponding image if available
    const image = data?.images?.[index];
    return {
      id: index + 1,
      thumbnail: image?.images_id?.image_file?.image?.id && image?.images_id?.image_file?.image?.filename_download 
        ? getReadableImageUrl(
            image.images_id.image_file.image.id, 
            image.images_id.image_file.image.filename_download
          )
        : ``, // Fallback if no image is available
      video: getReadableImageUrl(video.directus_files_id.id, video.directus_files_id.filename_download),
      alt: image?.images_id.alt || `Testimonial ${index + 1}`
    };
  }) || [];

  // Props to pass to client component
  const clientProps = {
    zIndex,
    thresholds,
    transitionTime,
    mobileThresholds,
    helperRotation,
    videosList,
    heading: data?.heading?.title || "",
    text: data?.text?.text || "",
    buttonText: data?.button?.label || "",
    buttonMobileText: data?.button_mobile?.label || "",
    buttonHref: data?.button?.href || "#",
    buttonMobileHref: data?.button_mobile?.href || "#",
    videoMobile: data?.video_mobile?.id ? getReadableImageUrl(data.video_mobile.id, data.video_mobile.filename_download || "") : ""
  };

  return (
    <ScreenAwareContent
      supportedContent={
        <VideoTestimonialsClient {...clientProps} />
      }
      fallbackContent={
        <VideoTestimonialsClientFallback {...clientProps} />
      }
    />
  );
}
